// src/components/resources/tools/generators/UtmBuilder/buildTaggedUrl.ts

/**
 * @fileoverview Pure URL-construction logic for the UTM Campaign URL Builder,
 * extracted from the component so it is unit-testable in isolation (the output
 * is correctness-critical — a broken tagged link ships silently into live
 * campaigns and only surfaces as wrong GA reports days later).
 */

/** The five canonical UTM parameters, in the order GA documents them. */
export type UtmKey = 'utm_source' | 'utm_medium' | 'utm_campaign' | 'utm_term' | 'utm_content'

export const UTM_KEYS: readonly UtmKey[] = [
  'utm_source',
  'utm_medium',
  'utm_campaign',
  'utm_term',
  'utm_content',
]

export type UtmValues = Record<UtmKey, string>

export interface BuildResult {
  url: string
  error: string | null
  warnings: string[]
}

/** Normalize a single UTM value when "clean values" is enabled. */
export function cleanValue(raw: string): string {
  return raw.trim().toLowerCase().replace(/\s+/g, '_')
}

/**
 * Construct the tagged URL.
 *
 * - Only `http:`/`https:` destinations are allowed — `new URL()` happily
 *   accepts `javascript:`, `file:`, `ftp:`, etc., which must never be emitted
 *   from a tool whose output is pasted into ads, email, and Slack.
 * - Existing query parameters and the hash fragment are preserved via
 *   `URLSearchParams` (robust against encoded `&`/`=` in affiliate/redirect
 *   values that a naive `split('&')` would corrupt). Any pre-existing `utm_*`
 *   keys are removed first so we never emit duplicates.
 */
export function buildTaggedUrl(baseUrl: string, values: UtmValues, clean: boolean): BuildResult {
  const warnings: string[] = []
  const trimmedBase = baseUrl.trim()

  if (!trimmedBase) {
    return { url: '', error: null, warnings }
  }

  // Does the input already carry a URL scheme (https://, mailto:, javascript:,
  // data:, …)? Anything with a real scheme is parsed as-is and checked against
  // the http/https allowlist below; only genuinely schemeless input gets the
  // https:// assumption.
  //
  // The wrinkle is a bare `host:port` (example.com:8080), which has a "word:"
  // prefix but is NOT a scheme. We must NOT treat every `:<digit>` as host:port,
  // though — `javascript:1` / `mailto:1` also match that and would slip through
  // as `https://javascript:1` (wrong host). So host:port is recognised narrowly:
  // a dotted hostname or `localhost`, followed by a pure numeric port. A
  // single-label `host:port` (no dot, not localhost) is treated as a scheme and
  // rejected — acceptably strict, since real campaign URLs use dotted domains.
  const startsWithScheme = /^[a-zA-Z][a-zA-Z\d+.-]*:/.test(trimmedBase)
  const looksLikeHostPort = /^(localhost|[a-z\d-]+(\.[a-z\d-]+)+):\d+([/?#]|$)/i.test(trimmedBase)
  const hasExplicitScheme = startsWithScheme && !looksLikeHostPort
  const withScheme = hasExplicitScheme ? trimmedBase : `https://${trimmedBase}`

  let url: URL
  try {
    url = new URL(withScheme)
  } catch {
    return { url: '', error: 'Enter a valid website URL (e.g. https://example.com/page).', warnings }
  }

  // Scheme allowlist — reject javascript:/file:/ftp:/data:/mailto: and anything
  // else that isn't a real web destination before we ever surface a Copy button.
  if (url.protocol !== 'http:' && url.protocol !== 'https:') {
    return { url: '', error: 'Only http and https URLs are supported.', warnings }
  }

  if (!hasExplicitScheme) {
    warnings.push('No scheme found — assumed https://.')
  }

  // Robust query handling: read/modify via URLSearchParams (the live object on
  // `url`), then append our values in canonical order. Re-serialization through
  // `url.toString()` keeps existing params and the hash fragment intact and
  // correctly encoded.
  const params = url.searchParams

  // Strip EVERY pre-existing utm_* key, not just the five this tool edits — a
  // retagged/copied URL can carry stale GA4 campaign metadata (utm_id,
  // utm_source_platform, utm_creative_format, …) that would otherwise survive
  // and silently attribute the new link to an old campaign.
  for (const key of [...params.keys()].filter(k => k.toLowerCase().startsWith('utm_'))) {
    params.delete(key)
  }

  for (const key of UTM_KEYS) {
    const raw = values[key]
    if (!raw.trim()) continue
    const value = clean ? cleanValue(raw) : raw.trim()
    if (!clean && /[A-Z]/.test(value)) {
      warnings.push(`${key} contains uppercase — GA treats "Facebook" and "facebook" as different sources.`)
    }
    if (!clean && /\s/.test(value)) {
      warnings.push(`${key} contains spaces — these can be mistyped and fragment your reports.`)
    }
    params.set(key, value)
  }

  return { url: url.toString(), error: null, warnings: Array.from(new Set(warnings)) }
}
